import { API_BASE } from '../config';
import { useEffect, useState, useRef, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { Server, Activity, MonitorSmartphone, BellOff } from 'lucide-react';

interface Device {
  id: string;
  hostname: string;
  os: string;
  arch: string;
  macAddress: string;
  ipAddress: string | null;
  localIp: string | null;
  publicIp: string | null;
  hardware: string | null;
  nickname: string | null;
  tags: string;
  status: string;
  lastSeen: string;
  silencedUntil: string | null;
}

export default function Dashboard() {
  const [devices, setDevices] = useState<Device[]>([]);
  const [totalDevices, setTotalDevices] = useState(0);
  const [page, setPage] = useState(0);
  const [search, setSearch] = useState('');
  const PAGE_SIZE = 50;
  const abortRef = useRef<AbortController | null>(null);

  const fetchDevices = useCallback(async () => {
    abortRef.current?.abort();
    const controller = new AbortController();
    abortRef.current = controller;
    try {
      const params = new URLSearchParams({
        limit: String(PAGE_SIZE),
        offset: String(page * PAGE_SIZE),
      });
      if (search) params.set('search', search);
      const res = await fetch(`${API_BASE}/api/devices?${params}`, {
        credentials: 'include',
        signal: controller.signal
      });
      if (res.ok) {
        const data = await res.json();
        setDevices(data.devices);
        setTotalDevices(data.total);
      }
    } catch (e) {
      if (e instanceof DOMException && e.name === 'AbortError') return;
      console.error(e);
    }
  }, [page, search]);

  useEffect(() => {
    fetchDevices();
    let interval = setInterval(fetchDevices, 30000);

    const onVisibilityChange = () => {
      if (document.hidden) {
        clearInterval(interval);
        abortRef.current?.abort();
      } else {
        fetchDevices();
        interval = setInterval(fetchDevices, 30000);
      }
    };
    document.addEventListener('visibilitychange', onVisibilityChange);

    return () => {
      clearInterval(interval);
      abortRef.current?.abort();
      document.removeEventListener('visibilitychange', onVisibilityChange);
    };
  }, [fetchDevices]);

  const onlineCount = devices.filter(d => d.status === 'online').length;
  const totalPages = Math.ceil(totalDevices / PAGE_SIZE);

  return (
    <div className="animate-fade-in" style={{ maxWidth: '1200px', margin: '0 auto' }}>
      <div className="grid-cards" style={{ marginBottom: '2rem' }}>
        <div className="glass-panel" style={{ padding: '1.5rem', display: 'flex', alignItems: 'center', gap: '1rem' }}>
          <div style={{ background: 'rgba(59, 130, 246, 0.1)', padding: '1rem', borderRadius: '50%' }}>
            <Server size={32} color="var(--accent-primary)" />
          </div>
          <div>
            <h3 style={{ color: 'var(--text-secondary)', fontSize: '0.875rem', fontWeight: 500 }}>Total Devices</h3>
            <p style={{ fontSize: '1.5rem', fontWeight: 600 }}>{totalDevices}</p>
          </div>
        </div>
        
        <div className="glass-panel" style={{ padding: '1.5rem', display: 'flex', alignItems: 'center', gap: '1rem' }}>
          <div style={{ background: 'rgba(16, 185, 129, 0.1)', padding: '1rem', borderRadius: '50%' }}>
            <Activity size={32} color="var(--success)" />
          </div>
          <div>
            <h3 style={{ color: 'var(--text-secondary)', fontSize: '0.875rem', fontWeight: 500 }}>Online</h3>
            <p style={{ fontSize: '1.5rem', fontWeight: 600 }}>{onlineCount}</p>
          </div>
        </div>
      </div>

      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
        <h3 style={{ fontWeight: 600, margin: 0 }}>Connected Devices</h3>
        <input
          type="text"
          placeholder="Search hostname or IP..."
          value={search}
          onChange={e => { setSearch(e.target.value); setPage(0); }}
          style={{ width: '250px', padding: '0.5rem 0.75rem', fontSize: '0.875rem' }}
        />
      </div>
      <div className="grid-cards">
        {devices.map(dev => (
          <Link to={`/devices/${dev.id}`} key={dev.id} className="glass-panel" style={{ padding: '1.5rem', display: 'block' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '1rem' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
                <MonitorSmartphone size={24} color="var(--text-secondary)" />
                <div>
                  <h4 style={{ fontSize: '1.125rem', fontWeight: 600, margin: 0 }}>{dev.nickname || dev.hostname}</h4>
                  {dev.nickname && <div style={{ fontSize: '0.75rem', color: 'var(--text-secondary)', marginTop: '2px' }}>{dev.hostname}</div>}
                </div>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', fontSize: '0.875rem', color: 'var(--text-secondary)' }}>
                {dev.silencedUntil && new Date(dev.silencedUntil) > new Date() && (
                  <span title={new Date(dev.silencedUntil).getFullYear() >= 9999 ? 'Silenced indefinitely' : `Silenced until ${new Date(dev.silencedUntil).toLocaleString()}`}><BellOff size={14} color="#eab308" /></span>
                )}
                <span>{dev.status}</span>
                <span className={`status-indicator status-${dev.status}`} />
              </div>
            </div>
            
            <div style={{ color: 'var(--text-secondary)', fontSize: '0.875rem' }}>
              <p><strong>OS:</strong> {dev.os} ({dev.arch})</p>
              <p><strong>IP:</strong> {dev.localIp || dev.publicIp || 'Unknown'}</p>
              <p><strong>Last Seen:</strong> {new Date(dev.lastSeen).toLocaleString()}</p>
            </div>
            {(() => { try { const t = JSON.parse(dev.tags || '[]'); return t.length > 0 ? (
              <div style={{ display: 'flex', gap: '0.35rem', flexWrap: 'wrap', marginTop: '0.5rem' }}>
                {t.map((tag: string) => (
                  <span key={tag} style={{ fontSize: '0.7rem', padding: '0.15rem 0.5rem', borderRadius: '10px', background: 'rgba(168, 85, 247, 0.15)', color: '#a855f7', fontWeight: 600 }}>{tag}</span>
                ))}
              </div>
            ) : null; } catch { return null; } })()}
          </Link>
        ))}
        {devices.length === 0 && (
          <div className="glass-panel" style={{ padding: '2rem', textAlign: 'center', gridColumn: '1 / -1' }}>
            <p style={{ color: 'var(--text-secondary)' }}>No devices found. Start an agent to see it here.</p>
          </div>
        )}
      </div>

      {totalPages > 1 && (
        <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: '1rem', marginTop: '1.5rem' }}>
          <button className="btn btn-secondary" onClick={() => setPage(p => Math.max(0, p - 1))} disabled={page === 0}>
            Previous
          </button>
          <span style={{ color: 'var(--text-secondary)', fontSize: '0.875rem' }}>
            Page {page + 1} of {totalPages}
          </span>
          <button className="btn btn-secondary" onClick={() => setPage(p => Math.min(totalPages - 1, p + 1))} disabled={page >= totalPages - 1}>
            Next
          </button>
        </div>
      )}
    </div>
  );
}
